'use client'

import { useEffect, useRef, type ReactNode } from 'react'
import { AnimatePresence, useInView } from 'motion/react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import Container from '@/components/structure/Container'
import Section from '@/components/content/common/Section'
import Pill from '@/components/ui/Pill'
import AppOverview from '@/components/content/app/AppOverview'
import Verdict from '@/components/ui/Verdict'
import Slider from '@/components/ui/Slider'
import { Icon } from '@/icons/Icon'
import { scrollToSection, toSlug } from '@/lib/utils'
import { generateStructure, renderContent } from '@/lib/content'
import { useScrollFold } from '@/hooks/useScrollFold'
import { easeMedium } from '@/lib/transitions'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { Block } from '@/types/app'
import type { App, Deal, ReviewCategoryCriterionRating, Category } from '@/payload-types'

type BestVerdictProps = {
  apps: App[]
  verdict: Block[]
  category: Category
  serverDataByAppId?: Map<
    number,
    {
      ratings: Pick<
        ReviewCategoryCriterionRating,
        'rating' | 'isApplicable' | 'categoryCriterion'
      >[]
      deals: Deal[]
    }
  >
}

export default function BestVerdict({
  apps,
  verdict,
  category,
  serverDataByAppId,
}: BestVerdictProps) {
  const { roots, preamble } = generateStructure({ content: verdict })
  const { sections, preamble: verdictPreamble } = renderContent({
    roots,
    preamble,
    rootTag: 3,
  })

  const { md } = useBreakpoint()
  const slideTotal = apps.length

  const verdictContent =
    apps.length > 0 &&
    apps.map((app, i) => (
      <AppOverview
        key={app.id}
        app={app}
        rank={i + 1}
        category={category}
        theme="dark"
        verdict={true}
        position="up"
        serverRatings={serverDataByAppId?.get(app.id)?.ratings}
        serverDeals={serverDataByAppId?.get(app.id)?.deals}
      />
    ))

  return (
    <>
      <VerdictPrompt />
      <div
        id="our-final-verdict"
        data-section-app={true}
        data-section-id="our-final-verdict"
        data-name="Our Final Verdict"
      >
        <Container className="relative mb-8 flex flex-col items-center gap-12 from-black via-white/50 to-black pt-20 before:absolute before:left-0 before:top-0 before:h-px before:w-full before:bg-gradient-to-r before:content-[''] md:mb-32 md:gap-20 md:pt-32">
          <h2 className="font-heading-regular text-36 text-white md:text-48">Our Final Verdict</h2>
          {apps.length > 0 && (
            <Verdict total={slideTotal}>
              {slideTotal > 2 || (slideTotal > 1 && !md) ? (
                <Slider reduced={true}>{verdictContent}</Slider>
              ) : (
                verdictContent
              )}
            </Verdict>
          )}

          <div className="relative z-10 flex max-w-content flex-col">
            {verdictPreamble && <div className="px-5 pb-4 text-zinc-300">{verdictPreamble}</div>}
            {sections.map((section, index) => {
              const sectionId = `our-final-verdict-${toSlug(section.title ?? `section-${index}`)}`

              return (
                <Section
                  key={sectionId}
                  sectionId={sectionId}
                  title={section.title || ''}
                  copy={false}
                  join={true}
                  depth={section.depth}
                  rootTag={section.rootTag}
                  offset={80}
                >
                  {section.content as ReactNode}
                </Section>
              )
            })}
          </div>
        </Container>
      </div>
    </>
  )
}

export function VerdictPrompt() {
  const verdictRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    verdictRef.current = document.getElementById('our-final-verdict')
  }, [])

  const isInView = useInView(verdictRef, { margin: '0px 0px -50px 0px' })
  const fold = useScrollFold({ threshold: 680 })
  const showPrompt = fold && !isInView

  return (
    <Motion>
      <AnimatePresence>
        {showPrompt && (
          <m.div
            key="showVerdictPrompt"
            className="fixed bottom-0 left-0 z-40 h-16 w-screen bg-gradient-to-t from-black to-transparent"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={easeMedium}
          >
            <div className="absolute left-1/2 top-0 w-fit -translate-x-1/2">
              <Pill
                style="invert"
                className="flex items-center gap-2.5"
                onClick={() =>
                  scrollToSection({
                    id: 'our-final-verdict',
                    offset: { mobile: 200, desktop: 80 },
                    animate: true,
                    hideHash: true,
                  })
                }
              >
                <Icon name="best" />
                <span>Go to our final verdict</span>
              </Pill>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </Motion>
  )
}
